import { useCallback, useEffect, useRef, useState } from "react";
import type {
  UserLifecycleAction,
  UserLifecyclePayload,
} from "../../api/admin-user-lifecycle";
import { t } from "../../i18n";
import { s } from "./user-detail-styles";

type FormState = {
  readonly reason: string;
  readonly policy_section: string;
  readonly incident_summary: string;
  readonly suspension_end: string;
  readonly appeal_deadline: string;
  readonly data_export_url: string;
};

type FieldKey = keyof FormState;

type Field = {
  readonly key: FieldKey;
  readonly multiline?: boolean;
  readonly optional?: boolean;
};

const EMPTY_FORM: FormState = {
  reason: "",
  policy_section: "",
  incident_summary: "",
  suspension_end: "",
  appeal_deadline: "",
  data_export_url: "",
};

export function UserLifecycleDialog({
  open,
  action,
  userName,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  action: UserLifecycleAction | null;
  userName: string;
  onConfirm: (payload: UserLifecyclePayload) => void;
  onCancel: () => void;
}): React.JSX.Element {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const fields = action ? fieldsForAction(action) : [];
  const canConfirm = action !== null && isValidForm(action, form);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
  }, [open]);

  const close = useCallback((): void => {
    setForm(EMPTY_FORM);
    onCancel();
  }, [onCancel]);

  const submit = useCallback((): void => {
    if (!action || !canConfirm) return;
    onConfirm(toPayload(action, form));
    setForm(EMPTY_FORM);
  }, [action, canConfirm, form, onConfirm]);

  const updateField = useCallback((field: FieldKey, value: string): void => {
    setForm((current) => ({ ...current, [field]: value }));
  }, []);

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="user-lifecycle-title"
      style={s.dialog}
      onCancel={(event) => {
        event.preventDefault();
        close();
      }}
    >
      <LifecycleDialogContent
        action={action}
        userName={userName}
        fields={fields}
        form={form}
        canConfirm={canConfirm}
        onFieldChange={updateField}
        onCancel={close}
        onSubmit={submit}
      />
    </dialog>
  );
}

function LifecycleDialogContent({
  action,
  userName,
  fields,
  form,
  canConfirm,
  onFieldChange,
  onCancel,
  onSubmit,
}: {
  readonly action: UserLifecycleAction | null;
  readonly userName: string;
  readonly fields: ReadonlyArray<Field>;
  readonly form: FormState;
  readonly canConfirm: boolean;
  readonly onFieldChange: (field: FieldKey, value: string) => void;
  readonly onCancel: () => void;
  readonly onSubmit: () => void;
}): React.JSX.Element {
  return (
    <div style={s.dialogContent}>
      <h2 id="user-lifecycle-title" style={s.dialogTitle}>
        {action ? t(`user_lifecycle.${action}.title`) : ""}
      </h2>
      <p style={s.dialogDescription}>
        {action
          ? t(`user_lifecycle.${action}.description`, { name: userName })
          : ""}
      </p>
      <div style={s.dialogGrid}>
        {fields.map((field) => (
          <LifecycleField
            key={field.key}
            field={field}
            value={form[field.key]}
            onChange={(value) => onFieldChange(field.key, value)}
          />
        ))}
      </div>
      <div style={s.dialogActions}>
        <button type="button" onClick={onCancel} style={s.cancelBtn}>
          {t("common.cancel")}
        </button>
        <button
          type="button"
          disabled={!canConfirm}
          onClick={onSubmit}
          style={{
            ...s.confirmDangerBtn,
            ...(canConfirm ? undefined : s.confirmBtnDisabled),
          }}
        >
          {action ? t(`user_lifecycle.${action}.confirm`) : ""}
        </button>
      </div>
    </div>
  );
}

function LifecycleField({
  field,
  value,
  onChange,
}: {
  field: Field;
  value: string;
  onChange: (value: string) => void;
}): React.JSX.Element {
  const inputId = `user-lifecycle-${field.key}`;
  return (
    <label htmlFor={inputId} style={s.dialogField}>
      <span style={s.dialogLabel}>
        {t(`user_lifecycle.field.${field.key}`)}
      </span>
      {field.multiline ? (
        <textarea
          id={inputId}
          rows={3}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          style={s.dialogTextarea}
        />
      ) : (
        <input
          id={inputId}
          type={inputTypeForField(field.key)}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          style={s.dialogInput}
        />
      )}
      {field.optional && (
        <span style={s.dialogHint}>{t("user_lifecycle.optional")}</span>
      )}
    </label>
  );
}

function fieldsForAction(action: UserLifecycleAction): ReadonlyArray<Field> {
  if (action === "issue-warning") {
    return [
      { key: "reason", multiline: true },
      { key: "policy_section" },
      { key: "incident_summary", multiline: true },
    ];
  }
  if (action === "suspend") {
    return [{ key: "reason", multiline: true }, { key: "suspension_end" }];
  }
  if (action === "terminate") {
    return [
      { key: "reason", multiline: true },
      { key: "appeal_deadline" },
      { key: "data_export_url", optional: true },
    ];
  }
  return [{ key: "reason", multiline: true }];
}

function isValidForm(action: UserLifecycleAction, form: FormState): boolean {
  return fieldsForAction(action)
    .filter((field) => !field.optional)
    .every((field) => {
      const value = form[field.key].trim();
      return field.key === "reason" ? value.length >= 10 : value.length > 0;
    });
}

function toPayload(
  action: UserLifecycleAction,
  form: FormState,
): UserLifecyclePayload {
  const payload: UserLifecyclePayload = { reason: form.reason.trim() };
  if (action === "issue-warning") {
    return {
      ...payload,
      policy_section: form.policy_section.trim(),
      incident_summary: form.incident_summary.trim(),
    };
  }
  if (action === "suspend") {
    return { ...payload, suspension_end: form.suspension_end.trim() };
  }
  if (action === "terminate") {
    return {
      ...payload,
      appeal_deadline: form.appeal_deadline.trim(),
      data_export_url: form.data_export_url.trim(),
    };
  }
  return payload;
}

function inputTypeForField(field: FieldKey): string {
  return field === "suspension_end" || field === "appeal_deadline"
    ? "datetime-local"
    : "text";
}
